/* Phase 10 — reacts to route changes and applies the correct theme:
 *  - /hospital/*                       → own hospital theme (JWT).
 *  - /patient/hospitals/:id            → that hospital's public theme.
 *  - /patient/doctors/:id              → resolve doctor → hospital, then apply.
 *  - /patient/appointments/:id/*       → resolve appointment → hospital, then apply.
 *  - everything else                   → platform default.
 */
import { useEffect, useRef } from "react";
import { matchPath, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useTheme } from "../context/ThemeProvider";
import { api } from "../lib/api";

// tiny in-memory resolver cache to avoid re-fetching doctor→hospital and appt→hospital
const doctorHospital = new Map();
const apptHospital = new Map();

async function resolveDoctorHospital(id) {
    if (doctorHospital.has(id)) return doctorHospital.get(id);
    try {
        const { data } = await api.get(`/patient/doctors/${id}`);
        const hid = data?.hospital?.id || null;
        if (hid) doctorHospital.set(id, hid);
        return hid;
    } catch { return null; }
}

async function resolveApptHospital(id) {
    if (apptHospital.has(id)) return apptHospital.get(id);
    try {
        const { data } = await api.get(`/patient/appointments/${id}`);
        const hid = data?.hospital_id || null;
        if (hid) apptHospital.set(id, hid);
        return hid;
    } catch { return null; }
}

export function ThemeRouteWatcher() {
    const location = useLocation();
    const { user } = useAuth();
    const { loadHospitalTheme, loadOwnHospitalTheme, resetToPlatformDefault } = useTheme();
    const runId = useRef(0);

    useEffect(() => {
        const myTicket = ++runId.current;
        const path = location.pathname;

        // Hospital admin's own dashboard
        if (path.startsWith("/hospital") && user?.role === "hospital_admin") {
            loadOwnHospitalTheme();
            return;
        }

        // Direct hospital detail
        const mHosp = matchPath("/patient/hospitals/:id", path);
        if (mHosp) {
            loadHospitalTheme(mHosp.params.id);
            return;
        }

        // Doctor detail — resolve to hospital
        const mDoc = matchPath("/patient/doctors/:id", path);
        if (mDoc) {
            (async () => {
                const hid = await resolveDoctorHospital(mDoc.params.id);
                if (myTicket === runId.current) {
                    if (hid) loadHospitalTheme(hid);
                    else resetToPlatformDefault();
                }
            })();
            return;
        }

        // Appointment detail / checkout / confirm — resolve to hospital
        const mAppt =
            matchPath("/patient/appointments/:id/checkout", path) ||
            matchPath("/patient/appointments/:id/confirm", path);
        if (mAppt) {
            (async () => {
                const hid = await resolveApptHospital(mAppt.params.id);
                if (myTicket === runId.current) {
                    if (hid) loadHospitalTheme(hid);
                    else resetToPlatformDefault();
                }
            })();
            return;
        }

        // Anything else → platform default
        resetToPlatformDefault();
    }, [location.pathname, user?.role, loadHospitalTheme, loadOwnHospitalTheme, resetToPlatformDefault]);

    return null;
}
